import { Auth } from "@aws-amplify/auth";
import Router from "next/router";
import { getEnvConfig } from "@/config/environments";
import { getSession, isExpired, removeJWT } from "@/services/authService";
import { JSONValue } from "@/types/json";
import { getErrorFromStatusCode } from "@/utils/error";
import { appendQueryParams } from "./url";

export type PaginatedResults<T> = {
  results: T[];
  paging?: {
    starting_after: string;
    ending_before: string;
    has_more: boolean;
  };
};

type HttpMethod = "GET" | "PATCH" | "POST" | "PUT" | "DELETE";

export const logout = (redirect = true) => {
  removeJWT();
  Auth.signOut();
  if (redirect) {
    Router.push("/signin");
  }
};

export const apiFetch = async <T>(
  url: string,
  method: HttpMethod = "GET",
  body?: BodyInit,
  headers?: HeadersInit
): Promise<T> => {
  const baseUrl = getEnvConfig().APIDomain;
  const jwt = getSession().jwt;

  if (isExpired(jwt)) {
    logout();
  }

  const response = await fetch(`${baseUrl}${url}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: jwt ? `Bearer ${jwt}` : "",
      "Canopy-App": "os",
      "Referer-Policy": "no-referrer",
      ...headers,
    },
    method,
    body,
  });

  if (!response.ok) {
    if (response.status === 401) {
      logout();
    }
    throw getErrorFromStatusCode(response.status);
  }

  if (response.headers.get("content-type")?.includes("text/csv")) {
    return response.blob() as T;
  }

  return (await response.json()) as T;
};

export type GetOptions = {
  query: {
    [key in string]?: string | number | boolean;
  };
  headers?: HeadersInit;
};

export const getOne = async <T>(url: string, options?: GetOptions): Promise<T> =>
  apiFetch(appendQueryParams(url, options?.query), "GET", undefined, options?.headers);

export const getPage = async <T, U = {}>(url: string, options?: GetOptions): Promise<PaginatedResults<T> & U> =>
  apiFetch(appendQueryParams(url, options?.query), "GET", undefined, options?.headers);

export const post = async <T>(url: string, body: JSONValue) => apiFetch<T>(url, "POST", JSON.stringify(body));
export const put = async <T>(url: string, body: JSONValue) => apiFetch<T>(url, "PUT", JSON.stringify(body));
export const patch = async <T>(url: string, body?: JSONValue) =>
  apiFetch<T>(url, "PATCH", body != null ? JSON.stringify(body) : undefined);
export const httpDelete = async <T>(url: string) => apiFetch<T>(url, "DELETE");
